// Deep-linkable sections (#20). Maps a short URL slug to the internal
// content-state string the portal switches on (see Content.jsx). A magic-link
// or notification can point at /p/<slug> to drop the user straight on that page
// instead of the default landing screen.
//
// This is purely a lookup consumed at render time by the /p/:section route —
// it adds no network calls and does not touch the login path, so it has no
// effect on how fast logging in or using the portal feels. Adding a new
// deep-link target is just one line here.
//
// Keys are lowercase, hyphenated. The route lowercases the incoming param, so
// /p/Payroll and /p/payroll both resolve.
export const SECTION_ROUTES = {
  payroll: "Payroll",
  stats: "Stats",
  tasks: "Tasks",
  points: "My Points",
  "my-points": "My Points",
  balance: "Balance",
  events: "Events",
  "event-defaults": "My Event Defaults",
  training: "Training",
  "clock-in": "Clock In",
  leads: "My Leads",
  "my-leads": "My Leads",
  // One Friend, one list (thread 1a11d3ef5edd21a9, Levi 2026-10-13: "It's only
  // 'Friend' now."). The second "My Friends" page that listed the tournament
  // Friends rows was folded into My Friends, which lists every Friend from
  // their Friend record; the old link opens My Friends.
  "my-recruits": "My Leads",
  store: "Kindred Store",
  network: "Network",
  resources: "Resources",
  tournaments: "Tournaments — View",
  "fantasy-admin": "Tournaments — Admin",
  // The tournament admin screen (/tournaments/admin/:id) is a deep-linkable,
  // shareable route — TournamentAdminLive copies the URL for sharing — and the
  // Back control on it is labelled "Admin list". That control now resolves to
  // a real destination, so the slug needs to exist here: without it
  // resolveSection returns null and the /p/<slug> route silently falls through
  // to the Home landing instead of opening the admin list (thread #1612 r4,
  // msg 1a1455f75ded3ee9).
  "tournaments-admin": "Tournaments — Admin",
  "tournament-admin": "Tournaments — Admin",
  // Notification Control Hub (Feature #11, Phase 3D). A push/in-app notification
  // can point at /p/notifications to drop the user straight onto the hub, and
  // /p/notifications?focus=<id> highlights the specific row (NotificationInbox
  // reads the ?focus= param). Both spellings resolve.
  notifications: "Notifications",
  notification: "Notifications",
  // Reminders (thread 1a1844a91564dba8): every reminder text links
  // /p/reminders?focus=<id>; an employee a reminder is linked to opens at
  // /p/directory?focus=<employee recid> (the Directory, filtered to them).
  reminders: "Reminders",
  reminder: "Reminders",
  directory: "Directory",
  // Sales Recording share deep-link: /p/sales-recordings?recording=<airtableId>
  // opens Sales Recordings and auto-opens the specified recording dialog.
  "sales-recordings": "Sales Recordings",
  recordings: "Sales Recordings",
  // Glitch notification deep-link: /p/glitches?focus=<id> opens the Glitches
  // page and auto-expands the specified glitch (GlitchesContent reads ?focus=),
  // so tapping "Open" on a "New glitch reported" / "Your glitch was fixed"
  // notification lands straight on that glitch.
  glitches: "Glitches",
  glitch: "Glitches",
  // "My Glitches" (thread 1a0588f4a47ab68c, Phase 1) — a submitter's own
  // reports, reachable at /p/my-glitches.
  "my-glitches": "My Glitches",
  // In-portal manual (V081, Levi 2026-10-01). /p/manual opens the manual, and
  // /p/manual?s=<slug> opens it with that entry expanded — which is the link the
  // Kai chat appends when it explains a feature, so the answer and the page it
  // points at are the same content.
  manual: "Portal Manual",
  // Inventory Admin (thread #1570, msg 1a11c82015cd5c7b, 2026-10-09). The page
  // renders, ButtonsForMobile dispatches its name and Content.jsx has the case,
  // but there was no entry here — so /p/inventory-admin resolved to null and
  // App.js's SectionDeepLink fell through to the /home landing instead of
  // opening the page. Nothing failed on the way: `resolveSection` returning null
  // IS the documented fallback, which is why the gap was silent. The nav
  // reachability guard (components/__tests__/navSections.test.js) does not close
  // it either — that suite checks each nav surface's own `case` label and reads
  // this file only for the routes it names.
  // Access is unchanged by this line: the BFF still gates every endpoint on the
  // "Inventory Admin" TABC row, so a caller without it lands on the page and
  // gets the page's own error, not records.
  "inventory-admin": "Inventory Admin",
  // Time Off - Manager (glitch #583, thread #1540 round 18, 2026-10-13). Same
  // shape as the entry above: Content.jsx has the case and the nav can dispatch
  // it, but with no line here /p/time-off-manager resolved to null and fell
  // through to the /home landing — so the one link that answers "where do I
  // approve this time-off request?" landed on the wrong page. The PTO approval
  // task is a NOTIFICATION (portal-bff:ptoApprovalTask) and this page is where
  // the decision is actually made, which is why the notification text names it.
  // Access is unchanged: the BFF still gates the endpoint, so a caller without
  // the right position gets the page's own error, not records.
  "time-off-manager": "Time Off - Manager",
  "pto-manager": "Time Off - Manager",
  // Label/SDS (Levi 2026-10-27, "Updating Items question"). The button, its
  // Content.jsx case and its drawer icon all exist, so the same silent gap the
  // two entries above describe applied here too: /p/label-sds resolved to null
  // and fell through to /home. This page is the one a Regional Operations
  // Manager is being handed to add SDS documents, so a link he can be sent
  // matters. Access is unchanged — the BFF re-checks the "Label/SDS" TABC row on
  // every call, so a caller without that Position gets the page and its own
  // error, never records.
  "label-sds": "Label/SDS",
  "label-sds-library": "Label/SDS",
};

// Resolve a URL slug to a content-state string, or null if unknown (the route
// then falls back to the default /home landing).
export function resolveSection(slug) {
  if (!slug) return null;
  return SECTION_ROUTES[String(slug).toLowerCase()] || null;
}
